'use client';
import { useRef } from 'react';

const UNSET = Symbol('useClientOnlyConstant/unset');

/**
 * Computes `factory()` exactly once, the first time this runs in the browser, and returns
 * `fallback` for every render before that — including a server/prerender render of this Client
 * Component, where `factory` reading a non-deterministic value (`uuid()`, `Date.now()`,
 * `Math.random()`, `crypto.*`) is disallowed under Next's `cacheComponents`. Only suitable for
 * values read later from event handlers or effects rather than rendered into markup, since the
 * one-time fallback-to-real-value swap on mount would otherwise cause a hydration mismatch.
 * See BNK-1108.
 */
export function useClientOnlyConstant<T>(factory: () => T, fallback: T): T {
  const ref = useRef<T | typeof UNSET>(UNSET);
  if (ref.current === UNSET) {
    ref.current = typeof window === 'undefined' ? fallback : factory();
  }
  return ref.current;
}
